import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Search, UserPlus, ChevronRight } from "lucide-react";
import { getCategoryIcon, useInventoryCategories } from "../inventory/categoryIcons";
import { EmployeeSignatureBadge, SIG_CARD_CLASS } from "../inventory/SignatureBadges";

import { EntraSyncButton } from "../shared/EntraSyncButton";
import { LeftCompanyBadge } from "../shared/LeftCompany";
import { hasLeftCompany } from "../../lib/utils";

const EMPTY_FORM = { first_name: "", last_name: "", email: "", employee_id: "", department_id: "", position: "", notes: "" };

export default function InventoryEmployeesView() {
    const navigate = useNavigate();
    const { departments, hasPermission } = useApp();
    const canManage = hasPermission("inventory:manage");
    const { categories } = useInventoryCategories();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [onlyWithItems, setOnlyWithItems] = useState(false);
    const [onlyUnsigned, setOnlyUnsigned] = useState(false);
    const [hideLeft, setHideLeft] = useState(false);
    const [visibleCount, setVisibleCount] = useState(48);
    const [createDialog, setCreateDialog] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.getInventoryEmployees();
            setEmployees(res.employees);
        } catch { toast.error("Erreur de chargement des employés"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const deptMap = React.useMemo(() => {
        const m = {};
        departments.forEach((d) => { m[d.id] = d.name; });
        return m;
    }, [departments]);

    const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const filtered = React.useMemo(() => {
        let list = employees;
        if (onlyWithItems) list = list.filter((e) => e.items_count > 0);
        if (onlyUnsigned) list = list.filter((e) => e.unsigned_items > 0);
        if (hideLeft) list = list.filter((e) => !hasLeftCompany(e) && e.is_active !== false);
        if (search.trim().length >= 2) {
            const q = normalize(search.trim());
            list = list.filter((e) => normalize(`${e.first_name} ${e.last_name} ${e.email || ""} ${e.employee_id || ""} ${deptMap[e.department_id] || ""}`).includes(q));
        }
        return list;
    }, [employees, search, onlyWithItems, onlyUnsigned, hideLeft, deptMap]);
    const leftCount = useMemo(() => employees.filter((e) => hasLeftCompany(e) || e.is_active === false).length, [employees]);

    const unsignedCount = React.useMemo(() => employees.filter((e) => e.unsigned_items > 0).length, [employees]);

    const handleCreate = async () => {
        if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Prénom et nom requis"); return; }
        setSaving(true);
        try {
            const emp = await api.createInventoryEmployee(form);
            toast.success(`Fiche de ${emp.first_name} ${emp.last_name} créée`);
            setCreateDialog(false);
            setForm(EMPTY_FORM);
            navigate(`/admin/inventory/employee/${emp.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="animate-pulse text-gray-400 py-8">Chargement des employés...</div>;

    return (
        <div data-testid="inventory-employees-view">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={(e) => { setSearch(e.target.value); setVisibleCount(48); }} placeholder="Rechercher un employé..." className="pl-9" data-testid="employee-search" />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <input type="checkbox" checked={onlyWithItems} onChange={(e) => setOnlyWithItems(e.target.checked)} className="rounded" data-testid="filter-with-items" />
                    Uniquement avec matériel
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <input type="checkbox" checked={onlyUnsigned} onChange={(e) => setOnlyUnsigned(e.target.checked)} className="rounded" data-testid="filter-unsigned" />
                    Remise non signée{unsignedCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 font-medium" data-testid="unsigned-count">{unsignedCount}</span>}
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <input type="checkbox" checked={hideLeft} onChange={(e) => setHideLeft(e.target.checked)} className="rounded" data-testid="filter-hide-left" />
                    Masquer les anciens collaborateurs{leftCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 font-medium" data-testid="left-count">{leftCount}</span>}
                </label>
                <div className="ml-auto flex items-center gap-2">
                    {canManage && <EntraSyncButton onDone={load} />}
                    {canManage && <Button onClick={() => { setForm(EMPTY_FORM); setCreateDialog(true); }} data-testid="create-employee-btn">
                        <UserPlus className="w-4 h-4 mr-1" />Nouvel employé
                    </Button>}
                </div>
            </div>

            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-xs text-gray-400">{filtered.length} employé(s)</p>
                <div className="flex items-center gap-3 text-[10px] text-gray-500" data-testid="signature-legend">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" />Remise signée</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500" />Partiellement signée</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" />Non signée</span>
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filtered.slice(0, visibleCount).map((e) => {
                    const catCounts = {};
                    (e.categories || []).forEach((c) => { catCounts[c] = (catCounts[c] || 0) + 1; });
                    return (
                        <button key={e.id}
                            onClick={(ev) => (ev.ctrlKey || ev.metaKey) ? window.open(`/admin/inventory/employee/${e.id}`, "_blank", "noopener") : navigate(`/admin/inventory/employee/${e.id}`)}
                            onAuxClick={(ev) => { if (ev.button === 1) { ev.preventDefault(); window.open(`/admin/inventory/employee/${e.id}`, "_blank", "noopener"); } }}
                            onMouseDown={(ev) => { if (ev.button === 1) ev.preventDefault(); }}
                            title="Clic molette ou Ctrl+clic : ouvrir dans un nouvel onglet"
                            className={`card text-left hover:shadow-md transition-shadow group ${SIG_CARD_CLASS[e.signed_status] || ""} ${hasLeftCompany(e) || e.is_active === false ? "bg-rose-50/40 border-rose-200" : ""}`} data-testid={`employee-card-${e.id}`} data-signed-status={e.signed_status || "none-items"} data-left-company={hasLeftCompany(e) ? "true" : "false"}>
                            <div className="card-body">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                        {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{e.first_name} {e.last_name}</p>
                                            {e.employee_id && <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono flex-shrink-0" title="N° employé" data-testid={`emp-card-id-${e.id}`}>N° {e.employee_id}</span>}
                                            {hasLeftCompany(e) ? <LeftCompanyBadge user={e} size="xs" /> : e.is_active === false && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium flex-shrink-0">Inactif</span>}
                                            {e.source === "local" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">Local</span>}
                                        </div>
                                        <p className="text-[11px] text-gray-400 truncate">{deptMap[e.department_id] || e.job_title || e.position || e.email || "—"}</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-teal-500 flex-shrink-0 mt-1" />
                                </div>
                                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50 gap-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {e.items_count === 0 ? (
                                            <span className="text-[10px] text-gray-300">Aucun équipement</span>
                                        ) : Object.entries(catCounts).map(([cat, n]) => {
                                            const Icon = getCategoryIcon(categories, cat);
                                            return <span key={cat} className="inline-flex items-center gap-0.5 text-[10px] text-gray-500"><Icon className="w-3 h-3" />{n}</span>;
                                        })}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <EmployeeSignatureBadge employee={e} testId={`employee-sig-${e.id}`} />
                                        {e.total_value > 0 && <span className="text-[10px] font-medium text-gray-500">{e.total_value.toLocaleString("fr-CH")} CHF</span>}
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {filtered.length > visibleCount && (
                <div className="text-center mt-4">
                    <Button variant="outline" size="sm" onClick={() => setVisibleCount(visibleCount + 48)} data-testid="show-more-employees">
                        Afficher plus ({filtered.length - visibleCount} restants)
                    </Button>
                </div>
            )}

            {/* Create local employee dialog */}
            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Nouvel employé (sans compte MS365)</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Prénom *</label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" /></div>
                            <div><label className="form-label text-xs">Nom *</label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" /></div>
                        </div>
                        <div><label className="form-label text-xs">Email (optionnel)</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Département</label>
                                <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="emp-department">
                                    <option value="">—</option>
                                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div><label className="form-label text-xs">Fonction</label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="emp-position" /></div>
                        </div>
                        <div><label className="form-label text-xs">N° d'employé</label><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="font-mono" placeholder="ex. 10452" data-testid="emp-employee-id" /></div>
                        <div><label className="form-label text-xs">Notes</label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="emp-notes" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
                        <Button onClick={handleCreate} disabled={saving} data-testid="confirm-create-employee-btn">{saving ? "..." : "Créer la fiche"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
